/**
 * Page permissions
 *
 * Which Roles & Permissions key opens each page. Mirrors the API's permission policy
 * (backend/src/config/permissionPolicy.js) so a page opens exactly when its data can load.
 * The sidebars use the same keys to decide which links to show.
 *
 * - First matching rule wins; ':param' matches one path segment, a trailing '/*' matches
 *   the path and everything below it.
 * - permission: a key, an array of keys (any one is enough), or null (open to everyone).
 * - Pages no rule matches keep their route's role check (e.g. super-admin-only settings).
 */

const PAGE_RULES = [
  // Dashboards and personal pages
  ['/', null],
  ['/dashboard', 'view_dashboard'],
  ['/retail/dashboard', 'view_dashboard'],
  ['/notifications', null],
  ['/settings/account', null],

  // Branches & inventory
  ['/analytics/consolidated', 'view_analytics'],
  ['/branches/:branchId/inventory', 'view_inventory'],
  ['/my-branch-inventory', 'view_inventory'],
  ['/my-branch', 'view_inventory'],
  ['/branches/*', 'view_branches'],
  ['/products/new', 'manage_products'],
  ['/products/:id/edit', 'manage_products'],
  ['/inactive-products', 'manage_products'],
  ['/products/*', 'view_products'],
  ['/catalogue', 'view_products'],
  ['/stock-transfers/*', 'view_inventory'],
  ['/stock-damages', 'view_inventory'],
  ['/categories/*', 'view_categories'],
  ['/accessory-categories', 'view_categories'],

  // Sales & POS
  ['/sales/new', 'create_sales'],
  ['/sales/analytics', 'view_analytics'],
  ['/sales/pending-confirmations', 'confirm_payments'],
  ['/sales/cancelled', 'reverse_sales'],
  ['/sales/*', ['view_sales', 'view_all_sales']],
  ['/receipts/settings', 'manage_receipts'],
  ['/receipts/*', 'view_receipts'],
  ['/calculator', 'access_pos'],

  // Customers & referrers
  ['/customers/new', 'create_customers'],
  ['/customers/:id/edit', 'update_customers'],
  ['/customers/*', 'view_customers'],
  ['/referrers/new', 'manage_referrers'],
  ['/referrers/:id/edit', 'manage_referrers'],
  ['/referrers/*', 'view_referrers'],
  ['/commissions', 'view_referrers'],
  ['/wholesalers/*', 'ws_view_customers'],

  // Tasks
  ['/performance-tasks/active', 'manage_performance_tasks'],
  ['/performance-tasks/inactive', 'manage_performance_tasks'],
  ['/performance-tasks/*', 'view_performance_tasks'],
  ['/my-performance-tasks', 'view_performance_tasks'],
  ['/tasks/*', 'view_tasks'],

  // Finance & accounting
  ['/expenses/new', 'manage_expenses'],
  ['/expenses/:id/edit', 'manage_expenses'],
  ['/expenses/analytics', 'view_expenses'],
  ['/expenses/*', 'view_expenses'],
  ['/expense-categories', 'view_expenses'],
  ['/budgets', 'view_budgets'],
  ['/accounting/bills/*', 'view_bills'],
  ['/accounting/invoices/*', 'view_invoices'],
  ['/accounting/vendors/*', 'view_vendors'],
  ['/accounting/*', 'view_accounting'],
  ['/payroll', 'manage_payroll'],
  ['/payroll/salaries/*', 'manage_payroll'],
  ['/payroll/runs/*', 'manage_payroll'],
  ['/reports/*', 'view_reports'],

  // Users & settings
  ['/users', 'view_users'],
  ['/activity-logs', 'view_audit_logs'],
  ['/recycle-bin', 'view_recycle_bin'],
  ['/purge-management', 'manage_recycle_bin'],
  ['/settings/bank-channels', 'view_banks'],
  ['/settings/branding', 'manage_settings'],
  ['/settings/tax', 'manage_settings'],
  ['/settings/fiscal-periods', 'view_accounting'],
  ['/settings/whatsapp', 'manage_settings'],
  ['/settings/reset', ['reset_retail_module', 'view_reports']],

  // Wholesale module
  ['/wholesale/dashboard', 'ws_view_dashboard'],
  ['/wholesale/orders/*', 'ws_view_orders'],
  ['/wholesale/whatsapp-orders', 'ws_view_orders'],
  ['/wholesale/distributors/*', 'ws_view_customers'],
  ['/wholesale/pricing-tiers', 'ws_view_pricing'],
  ['/wholesale/credit', 'ws_view_credit'],
  ['/wholesale/companies/*', 'ws_view_companies'],
  ['/wholesale/rebates', 'ws_view_companies'],
  ['/wholesale/products', 'ws_view_products'],
  ['/wholesale/payments', 'ws_view_payments'],
  ['/wholesale/invoices', 'ws_view_documents'],
  ['/wholesale/documents', 'ws_view_documents'],
  ['/wholesale/expenses/*', 'ws_view_expenses'],
  ['/wholesale/budgets', 'ws_view_expenses'],
  ['/wholesale/expense-categories', 'ws_view_expenses'],
  ['/wholesale/payroll', ['ws_view_payroll', 'ws_manage_payroll']],
  ['/wholesale/payroll/salaries/*', ['ws_view_payroll', 'ws_manage_payroll']],
  ['/wholesale/payroll/runs/*', ['ws_view_payroll', 'ws_manage_payroll']],
  ['/wholesale/accounting/*', 'ws_view_accounting'],
  ['/wholesale/reports/*', 'ws_view_reports'],
  ['/wholesale/settings/account', null],
  ['/wholesale/settings/users', 'view_users'],
  ['/wholesale/settings/pricing', 'ws_view_pricing'],
  ['/wholesale/settings/categories', 'ws_view_products'],
  ['/wholesale/settings/documents', 'ws_view_settings'],
  ['/wholesale/settings/audit-logs', 'view_audit_logs'],
  ['/wholesale/settings/bank-channels', 'ws_view_payments'],
  ['/wholesale/settings/fiscal-periods', 'ws_view_accounting'],
  ['/wholesale/settings/branding', 'ws_manage_settings'],
  ['/wholesale/settings/tax', 'ws_manage_settings'],
  ['/wholesale/settings/whatsapp', 'ws_manage_settings'],
  ['/wholesale/settings/email', 'ws_manage_settings'],
  ['/wholesale/settings/reset', ['ws_reset_module', 'ws_view_reports']],
  ['/wholesale/recycle-bin', 'view_recycle_bin'],
  ['/wholesale/purge-management', 'manage_recycle_bin']
].map(([pattern, permission]) => ({ pattern, permission, regex: compile(pattern) }));

function compile(pattern) {
  let source = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
  const hasWildcard = source.endsWith('/*');
  if (hasWildcard) source = source.slice(0, -2);
  source = source.replace(/:[A-Za-z_]+/g, '[^/]+');
  return new RegExp(`^${source}${hasWildcard ? '(?:/.*)?' : '/?'}$`);
}

const FULL_ACCESS_ROLES = ['super_admin', 'system_admin'];

/**
 * The permission rule for a page path, or undefined when no rule covers it
 * @returns {{permission: string|string[]|null}|undefined}
 */
export const resolvePagePermission = (pathname) => {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return PAGE_RULES.find((rule) => rule.regex.test(path));
};

/**
 * Whether the user may open the page.
 * @returns {boolean|null} null when no rule covers the page or the user's role permissions
 *   are unknown (callers then fall back to role checks)
 */
export const canOpenPage = (user, pathname) => {
  const rule = resolvePagePermission(pathname);
  if (!rule) return null;
  if (rule.permission === null) return true;
  if (FULL_ACCESS_ROLES.includes(user?.role)) return true;

  const permissions = user?.permissions;
  if (!permissions || typeof permissions !== 'object') return null;
  if (permissions.admin === true) return true;
  return [].concat(rule.permission).some((key) => permissions[key] === true);
};
